import { ArrowRight, Phone, ShieldCheck, Clock, Star } from "lucide-react";
import { BRAND } from "@/lib/site";
import { Button } from "@/components/ui/button";

const HERO_IMG =
  "https://images.unsplash.com/photo-1774274012714-ef0c28a9823f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxjYWxpZm9ybmlhJTIwaGlnaHdheSUyMGRyaXZpbmd8ZW58MHx8fHwxNzc5MTExMDE3fDA&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 pb-20 lg:pt-44 lg:pb-32 overflow-hidden bg-slate-50"
    >
      {/* Decorative dotted pattern */}
      <div className="absolute inset-0 bg-dots opacity-40 [mask-image:radial-gradient(ellipse_60%_60%_at_30%_30%,black,transparent)]" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
        {/* Left column */}
        <div className="lg:col-span-7 animate-fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-sm">
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-700">
              Open Today · San Diego, CA
            </span>
          </div>

          <h1 className="mt-6 font-display font-bold text-slate-900 text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
            Skip the DMV.
            <br />
            <span className="text-blue-600">Keep your day.</span>
          </h1>

          <p className="mt-7 text-lg sm:text-xl text-slate-600 max-w-2xl leading-relaxed">
            {BRAND.subTagline} Walk in, walk out — most registrations, title transfers,
            and renewals handled in <span className="font-semibold text-slate-900">under 30 minutes</span>.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-3" data-testid="hero-cta-row">
            <Button
              asChild
              data-testid="hero-book-button"
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg h-14 px-7 text-base font-semibold shadow-md shadow-blue-600/20"
            >
              <a href="#book" className="inline-flex items-center gap-2">
                Book an Appointment
                <ArrowRight className="h-5 w-5" />
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              data-testid="hero-call-button"
              className="bg-white text-slate-900 border-slate-300 hover:bg-slate-50 rounded-lg h-14 px-7 text-base font-semibold"
            >
              <a href={BRAND.phoneHref} className="inline-flex items-center gap-2">
                <Phone className="h-5 w-5" />
                Call {BRAND.phone}
              </a>
            </Button>
          </div>

          {/* Trust strip */}
          <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-4 text-sm text-slate-600">
            <div className="inline-flex items-center gap-2">
              <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
              <span className="font-semibold text-slate-900">5.0</span> from local customers
            </div>
            <div className="hidden sm:block h-4 w-px bg-slate-300" />
            <div className="inline-flex items-center gap-2">
              <Clock className="h-4 w-4 text-blue-600" />
              Same-day service available
            </div>
            <div className="hidden sm:block h-4 w-px bg-slate-300" />
            <div className="inline-flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-blue-600" />
              DMV-authorized partner
            </div>
          </div>
        </div>

        {/* Right column — visual */}
        <div className="lg:col-span-5 relative animate-fade-up" style={{ animationDelay: "120ms" }}>
          <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-slate-200 shadow-xl shadow-slate-900/5">
            <img
              src={HERO_IMG}
              alt="California highway"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent" />
            <div className="absolute bottom-0 inset-x-0 p-6">
              <div className="text-xs uppercase tracking-[0.22em] text-white/80 font-semibold">
                Your preferred registration center
              </div>
              <div className="mt-2 font-display text-white text-2xl font-semibold">
                Trusted on El Cajon Blvd since day one.
              </div>
            </div>
          </div>

          {/* Floating badge card */}
          <div className="absolute -left-4 lg:-left-10 top-8 bg-white border border-slate-200 rounded-2xl shadow-lg p-5 w-56 hidden md:block">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">
              Average visit
            </div>
            <div className="mt-2 font-display text-3xl font-bold text-slate-900">
              ~22 min
            </div>
            <div className="mt-1 text-xs text-slate-500">
              vs. ~2 hr at the DMV
            </div>
          </div>

          <div className="absolute -right-4 lg:-right-10 bottom-10 bg-slate-900 text-white rounded-2xl shadow-lg p-5 w-56 hidden md:block">
            <div className="text-xs uppercase tracking-[0.18em] text-white/60 font-semibold">
              Service hours
            </div>
            <div className="mt-2 font-display text-base font-semibold">
              Mon – Fri · 9–6
            </div>
            <div className="text-sm text-white/70">
              Sat · 10–1
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
